import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useNavigationGuard from "./useNavigationGuard";
import { useTranslation } from "react-i18next";
import ConfirmModal from "../../pages/Dashboard/Visitor/Invitation/Modal/ConfirmModal";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 * ChangeRequestGuard Component
 *
 * Displays a confirmation modal when the user attempts to navigate away
 * with unsaved changes. Integrates with a navigation guard hook and
 * optionally redirects after confirmation.
 *
 * @param {Object} props - Component properties
 * @param {boolean} [props.hasChanges=true] - Indicates if there are unsaved changes that require confirmation before leaving
 * @param {Function} [props.onConfirm] - Callback executed when the user confirms navigation (e.g., cleanup logic)
 * @param {string} [props.redirectUrl] - Optional URL to redirect after confirmation
 * @param {Array<string>} [props.allowedPaths] - List of paths where navigation is allowed without triggering the guard
 *
 * @returns {JSX.Element} Confirmation modal component controlling navigation behavior
 */
const ChangeRequestGuard = ({ hasChanges = true, onConfirm, redirectUrl, allowedPaths }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const {
        open,
        handleConfirm,
        handleCancel
    } = useNavigationGuard({
        when: hasChanges,
        onConfirmLeave: () => {
            onConfirm?.();
            toast.warn(t("all_the_recorded_information_was_lost"));

            if (redirectUrl) {
                navigate(redirectUrl);
            }
        },
        allowedPaths: allowedPaths
    });

    return (
        <ConfirmModal
            show={open}
            onHide={handleCancel}
            onClickFn={handleConfirm}
            title_modal='unsaved_changes'
            description={t('all_the_recorded_information_will_be_lost')}
            buttonCancelText={"stay"}
            buttonConfirm={"leave_the_page"}
        />
    );
};

export default ChangeRequestGuard;
